import { z } from 'zod';
import { FilterGroupSchema, GroupLogicSchema } from './filter';
import { FlowSortSchema, MAX_GROUP_LEVELS, normalizeGroupBy } from './view';

/**
 * Flow working-state — the dirty-relevant subset of a view's live state.
 *
 * This is BOTH the `parseDbJson()` schema for `flow_view_state.working_state`
 * (a per-user, per-entity JSON column — see U9) AND the client's
 * serialization shape for that same state. One schema, one source of truth.
 *
 * Deliberately excludes `search` — per the doc's dirty-state table, free-text
 * search is not part of a view's saved/working predicate and never
 * contributes to the "Modified" banner or gets persisted across sessions.
 */
export const FlowWorkingStateSchema = z.object({
  filter: z.array(FilterGroupSchema),
  groupLogic: GroupLogicSchema,
  sort: FlowSortSchema,
  /** Ordered, up-to-`MAX_GROUP_LEVELS` field-key list (R5) — same shape and
   *  same normalizer as `FlowViewSchema.groupBy` (view.ts). */
  groupBy: z.preprocess(normalizeGroupBy, z.array(z.string()).max(MAX_GROUP_LEVELS)),
  /** Ordered field-key list — visible columns, in order. */
  columns: z.array(z.string()),
  /**
   * Rep-dragged column widths in CSS pixels, keyed by column key (U10 /
   * R12). Deliberately lives HERE and nowhere else:
   *
   *  - It is NOT in `ViewDefinitionInput` (`view-normalize.ts`), so
   *    `viewDefinitionKey`/`viewDefinitionsEqual` cannot see it and a resize
   *    can never light the "Modified" banner — with zero special-casing.
   *  - It is NOT in the create/update view bodies (`definitionOf()` in
   *    `useViewStateMachine.ts` lists its keys explicitly), so widths never
   *    reach `flow_views` and can never leak into a view shared with the
   *    team. Widths are a per-rep, per-table preference, not part of a
   *    view's predicate.
   *
   * Optional: every row written before U10 lacks the key entirely and must
   * still parse (`parseDbJson` would otherwise degrade the whole row to
   * null and silently drop the rep's stored filters).
   */
  columnWidths: z.record(z.string(), z.number()).optional(),
});
export type FlowWorkingState = z.infer<typeof FlowWorkingStateSchema>;

/**
 * `GET /api/flow/view-state/:entity` response — the acting user's row for
 * that entity. All three fields are nullable: a user with no stored row for
 * an entity gets this exact null-shaped 200 (not a 404 — "no stored state"
 * is a normal, expected client state, not an error).
 *
 * `activeViewId`/`defaultViewId` are plain strings (never FKs) — they hold
 * either a code-defined preset slug (e.g. `'contact-all'`) or a
 * `flow_views.id` stringified, per the table's own doc comment.
 */
export const FlowViewStateResponseSchema = z.object({
  activeViewId: z.string().nullable(),
  defaultViewId: z.string().nullable(),
  workingState: FlowWorkingStateSchema.nullable(),
});
export type FlowViewStateResponse = z.infer<typeof FlowViewStateResponseSchema>;

/**
 * `PUT /api/flow/view-state/:entity` request body. Every field is optional
 * so a client can update just one pointer (e.g. "Set as default" writing
 * only `defaultViewId`) without resending the whole row; `.strict()` rejects
 * unknown keys the same way `FlowViewSchema` does.
 */
export const FlowViewStatePutBodySchema = z.object({
  activeViewId: z.string().nullable().optional(),
  defaultViewId: z.string().nullable().optional(),
  workingState: FlowWorkingStateSchema.nullable().optional(),
}).strict();
export type FlowViewStatePutBody = z.infer<typeof FlowViewStatePutBodySchema>;

/** Max length of a view id (saved-view row id or preset slug). Shared so `src/shared/**` schemas need not import from `src/api/`. */
/**
 * Every `entity` a view-state row can be keyed by (`GET/PUT /api/flow/view-state/:entity`). The meaning of
 * each key is documented in `src/api/flow-view-state.ts`; the client `FlowViewStateKey` union lists the same values.
 */
export const FLOW_VIEW_STATE_ENTITIES = [
  'contact',
  'account',
  'signal',
  'play',
  'contact_account',
  'contact_play',
  'plays',
  'contact_play_candidates',
  'contact_play_responded',
  'contact_play_not_responded',
] as const;

export const MAX_VIEW_ID_LENGTH = 128;

/**
 * True when an `activeViewId` points at a saved view (`flow_views.id`, a
 * stringified Int autoincrement) rather than a code preset slug. The two never
 * collide: preset slugs are non-numeric. The same predicate
 * `src/api/flow-views.ts` (`parseViewIdParam` / `isKnownViewId`) uses: digits
 * only AND a safe integer (a 20-digit string is not an addressable row id).
 */
export function isSavedViewId(id: string | null | undefined): id is string {
  return typeof id === 'string' && /^\d+$/.test(id) && Number.isSafeInteger(Number(id));
}
